import clsx from 'clsx'
import dayjs, { Dayjs } from 'dayjs'
import { nothing, PropertyValueMap, unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { html } from 'lit/static-html.js'

import { DdsElement } from '@/internal/dds-hu-element'
import { FocusableMixin } from '@/internal/mixin/focusableMixin'
import { t } from '@/localization/localization'

import styles from './calendar-cell.scss?inline'

/**
 * `dap-ds-calendar-cell`
 * @summary Calendar cell represents a single cell in the calendar.
 * @element dap-ds-calendar-cell
 * @title - Calendar cell
 * @group calendar
 *
 * @slot - The content of the calendar cell.
 *
 * @csspart base - The main calendar cell container.
 * @csspart today-mark - The today mark of the calendar cell.
 *
 * @cssproperty --dds-calendar-cell-display - The display property of the calendar cell host (default: flex)
 * @cssproperty --dds-calendar-cell-position - The position property of the calendar cell host (default: relative)
 * @cssproperty --dds-calendar-cell-border-radius - The border radius of the calendar cell (default: var(--dds-radius-rounded))
 * @cssproperty --dds-calendar-cell-min-width - The minimum width of the calendar cell (default: var(--dds-spacing-1000))
 * @cssproperty --dds-calendar-cell-height - The height of the calendar cell (default: var(--dds-spacing-1000))
 * @cssproperty --dds-calendar-cell-min-height - The minimum height of the calendar cell (default: var(--dds-spacing-1000))
 * @cssproperty --dds-calendar-cell-color - The text color of the calendar cell (default: var(--dds-button-subtle-text-neutral-enabled))
 * @cssproperty --dds-calendar-cell-font-size - The font size of the calendar cell (default: var(--dds-font-sm))
 * @cssproperty --dds-calendar-cell-font-weight - The font weight of the calendar cell (default: var(--dds-font-weight-medium))
 * @cssproperty --dds-calendar-cell-text-align - The text alignment of the calendar cell (default: center)
 * @cssproperty --dds-calendar-cell-cursor - The cursor style of the calendar cell (default: pointer)
 * @cssproperty --dds-calendar-cell-background-hover - The background color of the calendar cell on hover (default: var(--dds-background-neutral-medium))
 * @cssproperty --dds-calendar-cell-background-active - The background color of the calendar cell when active (default: var(--dds-background-neutral-strong))
 *
 * @cssproperty --dds-calendar-cell-selected-background - The background color of the selected calendar cell (default: var(--dds-button-primary-background-enabled))
 * @cssproperty --dds-calendar-cell-selected-color - The text color of the selected calendar cell (default: var(--dds-button-primary-text-enabled))
 * @cssproperty --dds-calendar-cell-selected-background-hover - The background color of the selected calendar cell on hover (default: var(--dds-button-primary-background-hover))
 * @cssproperty --dds-calendar-cell-selected-background-pressed - The background color of the selected calendar cell when pressed (default: var(--dds-button-primary-background-pressed))
 * @cssproperty --dds-calendar-cell-selected-today-mark-background - The background color of the today mark in selected calendar cell (default: var(--dds-text-neutral-on-inverted))
 *
 * @cssproperty --dds-calendar-cell-disabled-color - The text color of the disabled calendar cell (default: var(--dds-text-neutral-disabled))
 * @cssproperty --dds-calendar-cell-disabled-cursor - The cursor style of the disabled calendar cell (default: not-allowed)
 *
 * @cssproperty --dds-calendar-cell-out-of-range-color - The text color of the out-of-range calendar cell (default: var(--dds-text-neutral-placeholder))
 *
 * @cssproperty --dds-calendar-cell-today-font-weight - The font weight of the today calendar cell (default: var(--dds-font-weight-bold))
 *
 * @cssproperty --dds-calendar-cell-header-background - The background color of the header calendar cell (default: transparent)
 * @cssproperty --dds-calendar-cell-header-color - The text color of the header calendar cell (default: var(--dds-text-neutral-base))
 * @cssproperty --dds-calendar-cell-header-text-transform - The text transform of the header calendar cell (default: capitalize)
 * @cssproperty --dds-calendar-cell-header-cursor - The cursor style of the header calendar cell (default: default)
 *
 * @cssproperty --dds-calendar-cell-in-range-color - The text color of the in-range calendar cell (default: var(--dds-button-primary-text-enabled))
 * @cssproperty --dds-calendar-cell-in-range-background-hover - The background color of the in-range calendar cell on hover (default: var(--dds-button-primary-background-hover))
 * @cssproperty --dds-calendar-cell-in-range-background-pressed - The background color of the in-range calendar cell when pressed (default: var(--dds-button-primary-background-pressed))
 *
 * @cssproperty --dds-calendar-cell-range-background - The background color of the range start/end calendar cell (default: var(--dds-button-primary-background-enabled))
 * @cssproperty --dds-calendar-cell-range-color - The text color of the range start/end calendar cell (default: var(--dds-button-primary-text-enabled))
 * @cssproperty --dds-calendar-cell-range-today-mark-background - The background color of the today mark in range start/end calendar cell (default: var(--dds-text-neutral-on-inverted))
 *
 * @cssproperty --dds-calendar-cell-today-mark-bottom - The bottom position of the today mark (default: var(--dds-spacing-100))
 * @cssproperty --dds-calendar-cell-today-mark-width - The width of the today mark (default: 5px)
 * @cssproperty --dds-calendar-cell-today-mark-height - The height of the today mark (default: 5px)
 * @cssproperty --dds-calendar-cell-today-mark-stroke - The stroke color of the today mark (default: var(--dds-border-neutral-transparent))
 * @cssproperty --dds-calendar-cell-today-mark-background - The background color of the today mark (default: var(--dds-background-neutral-medium-inverted))
 * @cssproperty --dds-calendar-cell-today-mark-fill - The fill color of the today mark (default: var(--dds-background-neutral-medium-inverted))
 *
 */
@customElement('dap-ds-calendar-cell')
export default class DapDSCalendarCell extends FocusableMixin(DdsElement) {
  /** The value of the calendar cell. */
  @property({ attribute: false }) value: Dayjs
  /** Whether the calendar cell is selected. */
  @property({ type: Boolean }) selected: boolean = false
  /** Whether the calendar cell is disabled. */
  @property({ type: Boolean }) disabledDay: boolean = false
  /** Whether the calendar cell is today. */
  @property({ type: Boolean }) today: boolean = false
  /** Whether the calendar cell is out of range. */
  @property({ type: Boolean }) outOfRange: boolean = false
  /** Whether the calendar cell is a date header. */
  @property({ type: Boolean }) header: boolean = false
  /** Whether the calendar cell is focused. */
  @property({ type: Boolean }) focused: boolean = false
  /** Whether the calendar cell is the start of a range. */
  @property({ type: Boolean }) rangestart: boolean = false
  /** Whether the calendar cell is the end of a range. */
  @property({ type: Boolean }) rangeend: boolean = false
  /** Whether the calendar cell is in the middle of a range. */
  @property({ type: Boolean }) inrange: boolean = false

  /** The locale of the calendar cell.
   * @type {'hu' | 'en' | 'de'}
   * @default dayjs.locale()
   */
  @property({ reflect: true }) locale: string = dayjs.locale()

  private _cachedLabel: string = ''
  private _lastLabelUpdate: number = 0
  private _lastLocale: string = ''

  static readonly styles = unsafeCSS(styles)

  get label(): string {
    if (this.value) {
      const currentTime = Date.now()

      // Only recalculate if state changed, locale changed, or more than 100ms passed (debounce rapid updates)
      if (
        this._cachedLabel === '' ||
        this._lastLabelUpdate === 0 ||
        this._lastLocale !== this.locale ||
        currentTime - this._lastLabelUpdate > 100
      ) {
        // Cache expensive dayjs formatting
        const dateLabel = dayjs(this.value)
          .locale(this.locale)
          .format('LL, dddd')
        const todayLabel = this.today
          ? t('calendar.today', { lng: this.locale })
          : ''
        const selectedLabel = this.selected
          ? t('selected', { lng: this.locale })
          : ''
        this._cachedLabel = `${dateLabel} ${todayLabel} ${selectedLabel}`.trim()
        this._lastLabelUpdate = currentTime
        this._lastLocale = this.locale
      }

      return this._cachedLabel
    }

    return ''
  }

  public get focusElement(): HTMLElement {
    return this
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)

    // Remove aria-selected if it exists (not valid for button role)
    // The calendar component sets aria-pressed instead for button role
    if (this.role === 'button' && this.hasAttribute('aria-selected')) {
      this.removeAttribute('aria-selected')
    }

    this.setAttribute('aria-label', this.label)
  }

  protected updated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(_changedProperties)

    if (_changedProperties.has('disabledDay')) {
      this.setAttribute('aria-disabled', this.disabledDay ? 'true' : 'false')
    }

    if (
      _changedProperties.has('selected') ||
      _changedProperties.has('locale') ||
      _changedProperties.has('value') ||
      _changedProperties.has('today')
    ) {
      if (this.role === 'button' && this.hasAttribute('aria-selected')) {
        this.removeAttribute('aria-selected')
      }
      // Invalidate cache when locale changes
      if (_changedProperties.has('locale')) {
        this._cachedLabel = ''
        this._lastLabelUpdate = 0
      }
      this.setAttribute('aria-label', this.label)
    }
  }

  getDayName(dateStr: string, locale: string) {
    const date = new Date(dateStr)
    return date.toLocaleDateString(locale, { weekday: 'long' })
  }

  render() {
    return html`
      <div
        part="base"
        class=${clsx(
          'calendar-cell',
          { 'calendar-cell--selected': this.selected },
          { 'calendar-cell--disabled': this.disabledDay },
          { 'calendar-cell--today': this.today },
          { 'calendar-cell--out-of-range': this.outOfRange },
          { 'calendar-cell--header': this.header },
          { 'calendar-cell--focused': this.focused },
          { 'calendar-cell--range-start': this.rangestart },
          { 'calendar-cell--range-end': this.rangeend },
          { 'calendar-cell--in-range': this.inrange },
        )}>
        <slot></slot>
        ${this.today
          ? html`<span
              part="today-mark"
              class="calendar-cell__today-mark"></span>`
          : nothing}
      </div>
    `
  }
}
